import { useState, useEffect } from "react";
import api from "@/lib/api";

/**
 * Modal generico per i 3 template del flusso Iscrizioni Extra:
 *   - accettazione_pagamento → invia link + sposta stato a "in_attesa_pagamento"
 *   - lista_attesa            → sposta stato a "waiting"
 *   - conferma_pagamento      → sposta stato a "pagata" (richiede contabile caricata)
 *
 * Props:
 *   - iscrizione: { _id, cognome, nome, corso_codice, corso_descrizione, has_contabile? }
 *   - templateId: "accettazione_pagamento" | "lista_attesa" | "conferma_pagamento"
 *   - onClose(): chiamato in chiusura senza invio
 *   - onSent(result): chiamato dopo invio riuscito (result è la risposta API)
 */
const TEMPLATE_META = {
  accettazione_pagamento: {
    title: "📨 Mail Accettazione + link pagamento",
    headerBg: "#2e7d32",
    newStatoLabel: "INVIATO LINK PAGAMENTO",
    confirmText: "Stai per inviare la mail di ACCETTAZIONE con il link di pagamento. L'iscrizione passerà allo stato:",
  },
  lista_attesa: {
    title: "⏳ Mail Lista d'attesa",
    headerBg: "#ef6c00",
    newStatoLabel: "IN LISTA D'ATTESA",
    confirmText: "Stai per inviare la mail di LISTA D'ATTESA. L'iscrizione passerà allo stato:",
  },
  conferma_pagamento: {
    title: "💳 Mail Conferma pagamento",
    headerBg: "#1565c0",
    newStatoLabel: "CONFERMATA PAGATA",
    confirmText: "Stai per inviare la mail di CONFERMA PAGAMENTO. L'iscrizione passerà allo stato:",
  },
};

export default function MailTemplateModal({ iscrizione, templateId, onClose, onSent }) {
  const meta = TEMPLATE_META[templateId] || TEMPLATE_META.accettazione_pagamento;
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [candidates, setCandidates] = useState([]); // [{email, source, suggested}]
  const [selected, setSelected] = useState({}); // {email: true/false}
  const [extraEmails, setExtraEmails] = useState(""); // free-text textarea (comma/semicolon separated)
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [step, setStep] = useState("compose"); // compose | confirming | done
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!iscrizione?._id || !templateId) return;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const r = await api.get(`/mensa-matrice/extra-iscrizioni/${iscrizione._id}/prepara-template/${templateId}`);
        const cands = Array.isArray(r.data.candidates) && r.data.candidates.length > 0
          ? r.data.candidates
          : (r.data.to_list || []).map(em => ({ email: em, source: "suggerita", suggested: true }));
        setCandidates(cands);
        const initSel = {};
        const suggestedSet = new Set((r.data.to_list || []).map(s => String(s).toLowerCase()));
        cands.forEach(c => {
          const em = String(c.email || "").toLowerCase();
          initSel[em] = suggestedSet.size > 0 ? suggestedSet.has(em) : !!c.suggested;
        });
        setSelected(initSel);
        setExtraEmails("");
        setSubject(r.data.subject || "");
        setBody(r.data.body || "");
      } catch (e) {
        const status = e.response?.status;
        const detail = e.response?.data?.detail || e.message || "Errore sconosciuto";
        setErr(`${detail}${status ? ` (HTTP ${status})` : ""}`);
      } finally { setLoading(false); }
    })();
  }, [iscrizione?._id, templateId]);

  const toggleEmail = (em) => {
    const k = String(em || "").toLowerCase();
    setSelected(prev => ({ ...prev, [k]: !prev[k] }));
  };

  const buildToList = () => {
    const out = [];
    const seen = new Set();
    candidates.forEach(c => {
      const em = String(c.email || "").toLowerCase();
      if (selected[em] && !seen.has(em) && em.includes("@") && em.split("@")[1]?.includes(".")) {
        seen.add(em); out.push(em);
      }
    });
    extraEmails.split(/[,;\s\n]+/).forEach(t => {
      const em = String(t || "").trim().toLowerCase();
      if (em && !seen.has(em) && em.includes("@") && em.split("@")[1]?.includes(".")) {
        seen.add(em); out.push(em);
      }
    });
    return out;
  };

  const finalToList = buildToList();

  const invia = async () => {
    if (step === "compose") { setStep("confirming"); return; }
    setSending(true); setErr(null);
    try {
      if (finalToList.length === 0) { setErr("Seleziona almeno un destinatario"); setSending(false); setStep("compose"); return; }
      const r = await api.post(
        `/mensa-matrice/extra-iscrizioni/${iscrizione._id}/invia-template/${templateId}`,
        { to_list: finalToList, subject, body },
      );
      setStep("done");
      onSent && onSent(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore invio mail");
      setStep("compose");
    } finally { setSending(false); }
  };

  const renderSourceBadge = (src) => {
    const s = String(src || "");
    let bg = "#eceff1", col = "#37474f";
    if (s.startsWith("@canossadiaz")) { bg = "#e3f2fd"; col = "#1565c0"; }
    else if (s.startsWith("Anagrafica")) { bg = "#fff8e1"; col = "#e65100"; }
    else if (s === "iscrizione") { bg = "#f3e5f5"; col = "#6a1b9a"; }
    else if (s.startsWith("Archivio")) { bg = "#e8f5e9"; col = "#2e7d32"; }
    return (
      <span style={{ display: "inline-block", fontSize: 9, fontWeight: 700, letterSpacing: 0.3, padding: "2px 6px", borderRadius: 8, background: bg, color: col, marginLeft: 6, textTransform: "uppercase" }}>
        {s || "—"}
      </span>
    );
  };

  return (
    <div data-testid={`mail-template-modal-${templateId}`} style={overlay} onClick={onClose}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        <div style={{ ...header, background: meta.headerBg }}>
          <span>{meta.title} — {iscrizione?.cognome} {iscrizione?.nome}</span>
          <button onClick={onClose} data-testid="mtm-close" style={closeBtn}>✕</button>
        </div>
        <div style={body_style}>
          {loading ? (
            <div style={{ padding: 30, textAlign: "center", color: "#666" }}>Caricamento preview…</div>
          ) : (
            <>
              {err && (
                <div data-testid="mtm-error" style={{ padding: 8, background: "#ffebee", color: "#c62828", border: "1px solid #ef9a9a", borderRadius: 3, fontSize: 12, marginBottom: 10 }}>
                  ⚠ {err}
                </div>
              )}
              <Field label={`DESTINATARI — seleziona le email a cui inviare (${finalToList.length} selezionate)`}>
                {candidates.length === 0 ? (
                  <div style={{ fontSize: 12, color: "#999", fontStyle: "italic" }}>Nessuna email candidata trovata.</div>
                ) : (
                  <div data-testid="mtm-candidates" style={{ border: "1px solid #cfd8dc", borderRadius: 4, maxHeight: 180, overflowY: "auto", background: "#fafafa" }}>
                    {candidates.map((c, idx) => {
                      const em = String(c.email || "").toLowerCase();
                      const isSel = !!selected[em];
                      return (
                        <label key={em + idx}
                          data-testid={`mtm-cand-${idx}`}
                          style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderBottom: idx < candidates.length - 1 ? "1px solid #eceff1" : "none", cursor: "pointer", background: isSel ? "#fff" : "transparent" }}>
                          <input type="checkbox" checked={isSel} onChange={() => toggleEmail(em)}
                            data-testid={`mtm-cand-cb-${idx}`}
                            style={{ margin: 0 }} />
                          <span style={{ fontSize: 12, color: isSel ? "#212121" : "#888", fontWeight: isSel ? 600 : 400 }}>{em}</span>
                          {renderSourceBadge(c.source)}
                        </label>
                      );
                    })}
                  </div>
                )}
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#37474f", marginBottom: 3, letterSpacing: 0.3 }}>AGGIUNGI ALTRE EMAIL (opzionale, separate da virgola)</div>
                  <input data-testid="mtm-extra-emails"
                    value={extraEmails} onChange={e => setExtraEmails(e.target.value)}
                    placeholder="es. nonni@example.it, zio@example.it"
                    style={input_style} />
                </div>
              </Field>
              <Field label="OGGETTO">
                <input data-testid="mtm-subject" value={subject} onChange={e => setSubject(e.target.value)} style={input_style} />
              </Field>
              <Field label={templateId === "accettazione_pagamento" ? "CORPO (placeholder {link} verrà sostituito col link sicuro)" : "CORPO"}>
                <textarea data-testid="mtm-body" value={body} onChange={e => setBody(e.target.value)}
                  rows={14} style={{ ...input_style, fontFamily: "Consolas, monospace", fontSize: 11, resize: "vertical" }} />
              </Field>

              {step === "confirming" && (
                <div data-testid="mtm-confirming" style={{ padding: 12, background: "#fff8e1", border: "2px solid #ef6c00", borderRadius: 4, marginTop: 10 }}>
                  <div style={{ fontWeight: 700, color: "#bf360c", marginBottom: 6 }}>⚠️ CONFERMA OPERAZIONE</div>
                  <div style={{ fontSize: 12, color: "#5d4037", marginBottom: 8, lineHeight: 1.5 }}>
                    {meta.confirmText}<br />
                    → <b style={{ color: "#0d47a1" }}>{meta.newStatoLabel}</b><br />
                    <span style={{ fontSize: 11, color: "#5d4037" }}>Verrà inviata a <b>{finalToList.length}</b> destinatari: {finalToList.join(", ")}</span>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button data-testid="mtm-back" onClick={() => setStep("compose")} disabled={sending}
                      style={{ padding: "8px 14px", background: "white", border: "1px solid #888", fontSize: 12, fontWeight: 700, cursor: sending ? "wait" : "pointer", borderRadius: 3 }}>
                      ← Modifica
                    </button>
                    <button data-testid="mtm-final-send" onClick={invia} disabled={sending}
                      style={{ padding: "8px 18px", background: meta.headerBg, color: "white", border: "none", fontSize: 12, fontWeight: 800, cursor: sending ? "wait" : "pointer", borderRadius: 3, letterSpacing: 0.5 }}>
                      {sending ? "Invio…" : "✓ CONFERMA E INVIA"}
                    </button>
                  </div>
                </div>
              )}

              {step === "done" && (
                <div data-testid="mtm-done" style={{ padding: 12, background: "#e8f5e9", border: "2px solid #2e7d32", borderRadius: 4, marginTop: 10, color: "#1b5e20", fontWeight: 700 }}>
                  ✅ Mail inviata. Iscrizione → {meta.newStatoLabel}.
                </div>
              )}
            </>
          )}
        </div>
        {!loading && step === "compose" && (
          <div style={footer}>
            <button onClick={onClose} data-testid="mtm-cancel"
              style={{ padding: "8px 14px", background: "white", border: "1px solid #888", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
              Annulla
            </button>
            <button onClick={invia} data-testid="mtm-next" disabled={finalToList.length === 0 || !subject.trim() || !body.trim()}
              style={{ padding: "8px 18px", background: meta.headerBg, color: "white", border: "none", fontSize: 12, fontWeight: 800, cursor: "pointer", borderRadius: 3, letterSpacing: 0.5, opacity: (finalToList.length === 0 || !subject.trim() || !body.trim()) ? 0.5 : 1 }}>
              Anteprima OK · Procedi → ({finalToList.length})
            </button>
          </div>
        )}
        {step === "done" && (
          <div style={footer}>
            <button onClick={onClose} data-testid="mtm-finish"
              style={{ padding: "8px 18px", background: "#2e7d32", color: "white", border: "none", fontSize: 12, fontWeight: 800, cursor: "pointer", borderRadius: 3 }}>
              ✓ Chiudi
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 20 };
const modal = { background: "white", borderRadius: 6, width: "100%", maxWidth: 820, maxHeight: "92vh", display: "flex", flexDirection: "column", boxShadow: "0 10px 40px rgba(0,0,0,0.3)" };
const header = { padding: "10px 14px", color: "white", fontSize: 13, fontWeight: 700, display: "flex", justifyContent: "space-between", alignItems: "center", borderRadius: "6px 6px 0 0" };
const body_style = { padding: 14, overflow: "auto", flex: 1 };
const footer = { padding: "10px 14px", borderTop: "1px solid #e0e0e0", display: "flex", justifyContent: "flex-end", gap: 8, background: "#fafafa", borderRadius: "0 0 6px 6px" };
const closeBtn = { background: "transparent", border: "none", color: "white", fontSize: 18, cursor: "pointer", padding: "0 6px" };
const input_style = { width: "100%", padding: "7px 10px", border: "1px solid #ccc", fontSize: 12, boxSizing: "border-box", borderRadius: 3 };

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#37474f", marginBottom: 4, letterSpacing: 0.3 }}>{label}</div>
      {children}
    </div>
  );
}
